import {Card, DataStatus, Separator, Status} from 'platform/components';
import {Box, Grid, Show} from 'platform/foundation';
import {useFormatNumber} from 'platform/locale';

import {isNotNil} from 'ramda';
import {isNilOrEmpty} from 'ramda-adjunct';

import {useGetCustomerContractMaterialDiscountsQuery} from '@dms/api/customer';
import i18n from '@dms/i18n';

import {TestIdProps, suffixTestId} from 'shared/web';

import {SummaryItem} from '../../SummaryItem/SummaryItem';

interface ContractMaterialDiscountsSectionProps extends TestIdProps {
  customerContractId: string;
}

export function ContractMaterialDiscountsSection(props: ContractMaterialDiscountsSectionProps) {
  const formatNumber = useFormatNumber();

  const {data, isLoading, isError} = useGetCustomerContractMaterialDiscountsQuery({
    customerContractId: props.customerContractId,
  });

  return (
    <Card title={i18n.t('entity.customerContract.labels.materialDiscounts')}>
      <DataStatus isLoading={isLoading} isError={isError} minHeight={50}>
        <Show when={isNilOrEmpty(data?.materialDiscount)}>
          <Box paddingVertical={6}>
            <Status headline={i18n.t('general.labels.noData')} />
          </Box>
        </Show>
        {data?.materialDiscount?.map((discount, index) => (
          <Box key={discount?.id}>
            <Show when={index > 0}>
              <Separator />
            </Show>
            <Grid columns={4}>
              <SummaryItem
                isAlternative
                title={i18n.t('general.labels.name')}
                value={discount?.name}
                data-testid={suffixTestId('name', props)}
              />
              <SummaryItem
                isAlternative
                title={i18n.t('entity.customerContract.labels.discountType')}
                value={discount?.discountSettingsType?.name}
                data-testid={suffixTestId('discountType', props)}
              />
              <Show when={isNotNil(discount?.discountCustomerGroup)}>
                <SummaryItem
                  title={i18n.t('general.labels.discount')}
                  value={`${formatNumber(discount?.discountCustomerGroup ?? 0)} %`}
                  data-testid={suffixTestId('discountCustomerGroup', props)}
                />
              </Show>
              <Show when={isNotNil(discount?.discountOnSalePrice)}>
                <SummaryItem
                  title={i18n.t('general.labels.discount')}
                  value={`${formatNumber(discount?.discountOnSalePrice ?? 0)} %`}
                  data-testid={suffixTestId('discountOnSalePrice', props)}
                />
              </Show>
              <Show when={isNotNil(discount?.marginToPurchasePrice)}>
                <SummaryItem
                  title={i18n.t('general.labels.margin')}
                  value={`${formatNumber(discount?.marginToPurchasePrice ?? 0)} %`}
                  data-testid={suffixTestId('marginToPurchasePrice', props)}
                />
              </Show>
            </Grid>
          </Box>
        ))}
      </DataStatus>
    </Card>
  );
}
